import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Button, Badge } from 'reactstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate, formatFullDate } from 'utils/dateFormatter';
import InventoryFilterPanel from './InventoryFilterPanel';
import {
  InventoryFilterKey,
  InventoryFiltersState,
  countActiveFilters,
  filtersToApiParams,
  filtersToSearchParams,
  getFilterLabel,
  parseFiltersFromSearchParams,
  removeFilter,
} from './inventoryFilters';
interface Inventory {
  shopId: string;
  shopItemId: string;
  shopItemName: string;
  location: string;
  shelfLevel: number;
  quantity: number;
  sellPrice: number;
  isActive: boolean;
  deletedAt: boolean;
  updatedAt: string;
  nearestExpiryDate?: string | null;
}

const InventoryList: React.FC = () => {
  const { shop } = useShop();
  const { id: shopId } = shop!;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [appliedFilters, setAppliedFilters] = useState<InventoryFiltersState>(() =>
    parseFiltersFromSearchParams(searchParams),
  );

  useEffect(() => {
    setAppliedFilters(parseFiltersFromSearchParams(searchParams));
  }, [searchParams]);

  const appliedFilterCount = countActiveFilters(appliedFilters);
  const listEndpoint = API_ENDPOINTS.TENANT.INVENTORIES.PAGINATED.replace(':shopId', shopId || '');
  const queryKey = useMemo(() => {
    const qs = filtersToSearchParams(appliedFilters).toString();
    return qs ? `${listEndpoint}?${qs}` : listEndpoint;
  }, [listEndpoint, appliedFilters]);

  const handleApplyFilters = useCallback(
    (filters: InventoryFiltersState) => {
      setAppliedFilters(filters);
      setSearchParams(filtersToSearchParams(filters), { replace: true });
    },
    [setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleRemoveFilter = useCallback(
    (key: InventoryFilterKey) => {
      handleApplyFilters(removeFilter(appliedFilters, key));
    },
    [appliedFilters, handleApplyFilters],
  );

  const columns = useMemo<DataTableColumn<Inventory>[]>(
    () => [
      {
        accessorKey: 'shopItemName',
        id: 'shopItemName',
        header: 'Item Name',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => (
          <Link to={`/inventories/${info.row.original.shopItemId}`} className="text-dark fw-bold">
            {info.getValue() as string}
          </Link>
        ),
      },
      {
        accessorKey: 'quantity',
        id: 'quantity',
        header: 'Quantity',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => {
          const row = info.row.original;
          const qty = info.getValue() as number;
          const belowShelf = qty <= row.shelfLevel;
          return (
            <span className={`fw-semibold ${belowShelf ? 'text-warning' : ''}`}>
              {qty}
              {belowShelf && (
                <i
                  className="bx bx-down-arrow-alt ms-1 text-warning"
                  title="At or below shelf level"
                />
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'sellPrice',
        id: 'sellPrice',
        header: 'Sell Price',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => {
          const price = info.getValue() as number;
          return <span>{price?.toFixed(2)} PKR</span>;
        },
      },
      {
        accessorKey: 'location',
        id: 'location',
        header: 'Location',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => <span>{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'shelfLevel',
        id: 'shelfLevel',
        header: 'Shelf Level',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => (
          <span className="badge bg-secondary">{info.getValue() as number}</span>
        ),
      },
      {
        accessorKey: 'isActive',
        id: 'isActive',
        header: 'Status',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => {
          const isActive = info.getValue() as boolean;
          return (
            <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        accessorKey: 'nearestExpiryDate',
        id: 'nearestExpiryDate',
        header: 'Nearest Expiry',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => {
          const expiryDate = info.getValue() as string | null | undefined;
          return expiryDate ? (
            formatFullDate(expiryDate, false)
          ) : (
            <span className="text-muted">-</span>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return <span className={`small ${isStale ? 'text-danger' : 'text-muted'}`}>{text}</span>;
        },
      },
      {
        accessorKey: 'actions',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<Inventory, unknown>) => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const url = SHOP_PATHS.EDIT_INVENTORY.replace(':shopId', shopId || '').replace(
                    ':itemId',
                    row.shopItemId,
                  );
                  navigate(url);
                }}
              >
                <i className="ri-edit-line"></i> Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (window.confirm(`Deactivate inventory for item "${row.shopItemName}"?`)) {
                    console.log('Delete inventory:', row.shopItemId);
                  }
                }}
              >
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          );
        },
      },
    ],
    [shopId, navigate],
  );

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<Inventory>> => {
    return DataTableApiService.fetchPaginatedData<Inventory>(listEndpoint, {
      ...params,
      ...filtersToApiParams(appliedFilters),
    });
  };

  const handleAddInventory = () => {
    const url = SHOP_PATHS.ADD_INVENTORY.replace(':shopId', shopId || '');
    navigate(url);
  };

  const activeFilterKeys = Object.entries(appliedFilters)
    .filter(([, v]) => v)
    .map(([k]) => k as InventoryFilterKey);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">Inventory Management</h5>
              <button className="btn btn-success" onClick={handleAddInventory}>
                <i className="ri-add-line"></i> Add Inventory
              </button>
            </div>

            {appliedFilterCount > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {activeFilterKeys.map((key) => (
                  <Badge
                    key={key}
                    color="primary"
                    pill
                    className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  >
                    {getFilterLabel(key, appliedFilters)}
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      style={{ fontSize: '0.65rem' }}
                      aria-label={`Remove ${getFilterLabel(key)} filter`}
                      onClick={() => handleRemoveFilter(key)}
                    />
                  </Badge>
                ))}
                <Button color="link" className="p-0 text-muted small" onClick={handleClearFilters}>
                  Clear all
                </Button>
              </div>
            )}

            <DataTable
              columns={columns}
              queryKey={queryKey}
              onFetchData={handleFetchData}
              enableSearch={true}
              enableFiltering={false}
              enablePagination={true}
              enableSorting={true}
              enableColumnVisibility={true}
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport={true}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="inventory_items_visibility"
              columnOrderStorageKey="inventory_items_order"
              title=""
              emptyMessage={
                appliedFilterCount > 0
                  ? 'No inventory items match the selected filters.'
                  : 'No inventory items found. Add inventory to get started.'
              }
              striped={true}
              hover={true}
              bordered={true}
              responsive={true}
              toolbarExtra={
                <InventoryFilterPanel filters={appliedFilters} onApply={handleApplyFilters} />
              }
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InventoryList;
